import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Auth slice — Zustand-backed Cognito session.
 *
 * Holds the three Cognito tokens (ID, access, refresh) plus the absolute
 * expiry epoch in ms. `apiFetch` uses `expiresAt` to refresh proactively
 * before each request and to flip `isAuthenticated` to false on 401.
 *
 * `tenantId` mirrors the `custom:tenant_id` claim on the ID token; every
 * authenticated request includes it and the API authorizer enforces it.
 */

export type UserRole = 'trainee' | 'instructor' | 'org_admin' | 'superadmin';

export interface AuthUser {
  userId: string;
  email: string;
  tenantId: string;
  role: UserRole;
}

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  /** Absolute epoch-ms when the ID/access tokens expire. */
  expiresAt: number;
}

export interface AuthState {
  user: AuthUser | null;
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Actions
  signIn: (user: AuthUser, tokens: AuthTokens) => void;
  /** Replace the short-lived tokens without disturbing user identity. */
  refreshTokens: (tokens: Pick<AuthTokens, 'idToken' | 'accessToken' | 'expiresAt'>) => void;
  signOut: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      idToken: null,
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      isAuthenticated: false,
      isLoading: false,
      signIn: (user, tokens) =>
        set({
          user,
          idToken: tokens.idToken,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: tokens.expiresAt,
          isAuthenticated: true,
          isLoading: false,
        }),
      refreshTokens: (tokens) =>
        set({
          idToken: tokens.idToken,
          accessToken: tokens.accessToken,
          expiresAt: tokens.expiresAt,
        }),
      signOut: () =>
        set({
          user: null,
          idToken: null,
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          isAuthenticated: false,
          isLoading: false,
        }),
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'forge-auth',
      // SECURITY: the long-lived refreshToken is NEVER persisted to localStorage
      // (any XSS reading it = durable account takeover). It lives in memory only;
      // the ~1h idToken persists so a reload keeps the session until it expires.
      partialize: (state) => ({
        user: state.user,
        idToken: state.idToken,
        accessToken: state.accessToken,
        expiresAt: state.expiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

// Convenience selectors — avoid re-renders when only a slice is needed.
export const selectTenantId = (state: AuthState) => state.user?.tenantId ?? null;
export const selectRole = (state: AuthState) => state.user?.role ?? null;
